import { Tooltip } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { trimZeros } from "src/utils/genericFunctions";
import { InputWithText } from "./inputWithText";
import theme from "src/themes";

/** Default upper bound when no column cap is configured (matches InputItem). */
export const NUMERIC_INPUT_DEFAULT_MAX = 999999;

/** Preserve intentional empty string from row (e.g. MT row awaiting pricing); avoid `"" || 0`. */
export const valueFromRowDataField = (rowData: any, fieldId: string) => {
  const raw = rowData[fieldId];
  return raw === "" ? "" : raw ?? 0;
};

type NumericInputWithMaxProps = {
  itemId: string | number;
  rowData: any;
  editMode: boolean;
  id: string;
  onBlur?: (item: string, id: string | number, value: any) => any;
  disableDecimalDigit?: boolean;
  sxConfig?: any;
  disabled?: boolean;
  /** Inclusive maximum for the numeric value while typing. */
  numericMax?: number;
  errorMessage?: string;
};

/**
 * Same behavior as InputItem, with a configurable inclusive numeric maximum.
 * ComponentsMap costInput always uses this; row-driven cap via numericMaxFromRowKey, else default max.
 */
const NumericInputWithMax = ({
  itemId,
  rowData,
  editMode,
  id,
  disabled,
  onBlur,
  errorMessage,
  sxConfig = {},
  disableDecimalDigit = false,
  numericMax = NUMERIC_INPUT_DEFAULT_MAX
}: NumericInputWithMaxProps) => {
  const { t } = useTranslation();
  const effectiveMax =
    typeof numericMax === "number" &&
      !Number.isNaN(numericMax) &&
      numericMax >= 0
      ? numericMax
      : NUMERIC_INPUT_DEFAULT_MAX;

  /** Only show max-exceeded tooltip when a cap stricter than the generic default is in effect. */
  const shouldShowMaxExceededTooltip =
    Boolean(errorMessage) && effectiveMax !== NUMERIC_INPUT_DEFAULT_MAX;

  useEffect(() => {
    setValue(valueFromRowDataField(rowData, id));
  }, [id, rowData]);

  const [value, setValue] = useState(() => valueFromRowDataField(rowData, id));
  const [showError, setShowError] = useState(false);

  const handleChange = (text: string) => {
    let next = trimZeros(text);
    const [beforeDecimal, afterDecimal] = next.split(".");
    if (disableDecimalDigit) next = beforeDecimal;
    if (afterDecimal?.length > 2) return;
    if (next.includes(".") && beforeDecimal === "") {
      next = "0" + next;
    }

    if (next === "") {
      setValue(0);
      return;
    }
    if (Number(next) >= 0 && Number(next) <= effectiveMax) {
      setShowError(false);
      setValue(next);
    } else {
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 4000);
    }
  };

  const blurHandler = (_receivedValue: string) => {
    if (_receivedValue === "") {
      setValue(0);
    }
    onBlur(id, itemId, Number(value));
  };

  const tooltipText = t(errorMessage, { max: effectiveMax });
  const tooltipTitle = (
    <>
      <span style={{ color: theme.palette.error.main }}>
        {t("manualTransfer.poItems.note")}
      </span>
      {tooltipText}
    </>
  );

  return (
    <Tooltip
      title={tooltipTitle}
      open={shouldShowMaxExceededTooltip && showError}
      arrow
      placement="bottom"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: theme.palette.grey[100],
            color: theme.palette.utility.black
          }
        },
        arrow: {
          sx: {
            color: theme.palette.grey[100]
          }
        }
      }}
    >
      <div style={{ position: "relative" }}>
        <InputWithText
          editMode={editMode}
          disabled={disabled || rowData?.pricingLoading}
          value={value}
          handleChange={handleChange}
          blurHandler={blurHandler}
          sxConfig={sxConfig}
          rowData={rowData}
          id={id}
        />
      </div>
    </Tooltip>
  );
};

export default NumericInputWithMax;
